import { Coords, Size, Scroll, TileOrigin } from 'src/types';
import { CoordsUtils } from 'src/utils/CoordsUtils';
import { SizeUtils } from 'src/utils/SizeUtils';
import { GridBackgroundPosition, GridCalculationParams, GridConfig } from './types';

const normalizeZero = (value: number): number =>
    Object.is(value, -0) ? 0 : value;

/**
 * IsometricGrid - Core grid calculation logic
 * Pure TypeScript class with no React or UI dependencies
 */
export class IsometricGrid {
    private config: GridConfig;

    constructor(config: GridConfig) {
        this.config = config;
    }

    /**
     * Calculates the projected tile size based on zoom level
     */
    calculateTileSize(zoom: number): Size {
        return SizeUtils.multiply(this.config.projectedTileSize, zoom);
    }

    /**
     * Calculates the background position for grid rendering
     */
    calculateBackgroundPosition(
        scroll: Scroll,
        zoom: number,
        rendererSize: Size
    ): GridBackgroundPosition {
        const tileSize = this.calculateTileSize(zoom);

        const position: Coords = {
            x: rendererSize.width / 2 + scroll.position.x + tileSize.width / 2,
            y: rendererSize.height / 2 + scroll.position.y
        };

        return {
            x: position.x,
            y: position.y,
            tileSize
        };
    }

    /**
     * Converts screen coordinates to isometric tile coordinates
     */
    screenToTile(params: GridCalculationParams & { mouse: Coords }): Coords {
        const { mouse, zoom, scroll, rendererSize } = params;
        const projectedTileSize = this.calculateTileSize(zoom);
        const halfW = projectedTileSize.width / 2;
        const halfH = projectedTileSize.height / 2;

        const projectPosition = {
            x: -rendererSize.width * 0.5 + mouse.x - scroll.position.x,
            y: -rendererSize.height * 0.5 + mouse.y - scroll.position.y
        };

        const tile = {
            x: normalizeZero(Math.floor(
                (projectPosition.x + halfW) / projectedTileSize.width -
                projectPosition.y / projectedTileSize.height
            )),
            y: normalizeZero(-Math.floor(
                (projectPosition.y + halfH) / projectedTileSize.height +
                projectPosition.x / projectedTileSize.width
            ))
        };

        return tile;
    }

    /**
     * Gets the screen position of a tile based on its origin point
     */
    getTilePosition(tile: Coords, origin: TileOrigin = 'CENTER'): Coords {
        const halfW = this.config.projectedTileSize.width / 2;
        const halfH = this.config.projectedTileSize.height / 2;

        const position: Coords = {
            x: normalizeZero(halfW * tile.x - halfW * tile.y),
            y: normalizeZero(-(halfH * tile.x + halfH * tile.y))
        };

        switch (origin) {
            case 'TOP':
                return CoordsUtils.add(position, { x: 0, y: -halfH });
            case 'BOTTOM':
                return CoordsUtils.add(position, { x: 0, y: halfH });
            case 'LEFT':
                return CoordsUtils.add(position, { x: -halfW, y: 0 });
            case 'RIGHT':
                return CoordsUtils.add(position, { x: halfW, y: 0 });
            case 'CENTER':
            default:
                return position;
        }
    }

    /**
     * Converts isometric tile coordinates to screen coordinates
     */
    tileToScreen(
        tile: Coords,
        origin: TileOrigin,
        rendererSize: Size
    ): Coords {
        const position = this.getTilePosition(tile, origin);

        return {
            x: position.x + rendererSize.width / 2,
            y: position.y + rendererSize.height / 2
        };
    }

    /**
     * Gets the unprojected tile size
     */
    getUnprojectedTileSize(): number {
        return this.config.unprojectedTileSize;
    }

    /**
     * Gets the projected tile size (without zoom)
     */
    getProjectedTileSize(): Size {
        return this.config.projectedTileSize;
    }

    /**
     * Gets the projection multipliers
     */
    getProjectionMultipliers(): Size {
        return this.config.projectionMultipliers;
    }
}
